import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Building } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import {
  currentAnnotation,
  currentPovState,
  povPerPointState
} from 'store/krpanoStore'
import currentViewerState from 'store/viewerStore'
import Krpano from 'utils/krpano'

const MOBILE_FOV_FACTOR = 1.6
// Default FOV value set by the backend – only apply mobile factor when FOV equals this
const DEFAULT_FOV = 100

// Client-only FOV adjust helper (uses window.innerWidth so safe on reload).
// Only multiplies MOBILE_FOV_FACTOR when FOV is the default value (100).
// If user/admin has set a custom FOV, keep it unchanged.
const adjustFovForMobileClient = (z: number | string) => {
  const zNum = Number(z)
  if (Number.isNaN(zNum)) return zNum

  const isClient =
    typeof window !== 'undefined' && typeof window.innerWidth === 'number'
  const isMobile = isClient ? window.innerWidth < 768 : false

  return isMobile && zNum === DEFAULT_FOV ? zNum * MOBILE_FOV_FACTOR : zNum
}

export default function useThumbnailCarousel({
  building,
  setSelectedPointID
}: {
  building: Building | undefined
  setSelectedPointID: Dispatch<SetStateAction<string>>
}) {
  const points = useRecoilValue(listPointState)
  const [isOpen, setIsOpen] = useState(true)

  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [povPerPoint, setPovPerPoint] = useRecoilState(povPerPointState)
  const [, setCurrentSelected] = useRecoilState(currentAnnotation)

  // ID of the image currently being displayed.
  const currentlyLoadedImageIdRef = useRef<number | null>(null)

  // Track selectedImage to update currentlyLoadedImageIdRef for the first time
  useEffect(() => {
    if (
      currentViewer.selectedImage &&
      currentlyLoadedImageIdRef.current === null
    ) {
      currentlyLoadedImageIdRef.current = currentViewer.selectedImage
    }
  }, [currentViewer.selectedImage])

  const directionList = useMemo(
    () =>
      currentViewer.pointDetail?.directions
        ?.map((item) => {
          const order = points.find(
            (p) =>
              p.id !== currentViewer.selectedPoint && item.id.includes(p.id)
          )?.order
          return !order ? { ...item, target: null } : { ...item, target: order }
        })
        .filter((item: any) => item.target),
    [currentViewer.pointDetail?.directions, currentViewer.selectedPoint, points]
  )

  const stairList = useMemo(
    () =>
      currentViewer.pointDetail?.stairways?.map((item) => ({
        ...item,
        nextFloorName: building?.floors.find(
          (floor) => floor.id === +item.nextFloorId
        )?.name,
        areaId: currentViewer.pointDetail?.areaId
      })),
    [
      building?.floors,
      currentViewer.pointDetail?.stairways,
      currentViewer.pointDetail?.areaId
    ]
  )

  /**
   * When a thumbnail is clicked, this function is called with the ID of the point
   * represented by the thumbnail. The function sets the selected point ID to the
   * given point ID and calls `handleSelectPoint` with the given point ID.
   *
   * @param pointId The ID of the point represented by the clicked thumbnail.
   */
  const handleThumbnailClick = (pointId: string) => {
    const tourViewerKrpano = document.getElementById('embedpano-viewer') as any
    const isLoadComplete = tourViewerKrpano?.get('loadcomplete')
    if (!tourViewerKrpano) return

    if (currentViewer.selectedPoint === pointId) {
      const mainImageId = currentViewer.pointDetail?.image360?.id
      const sourceImageId =
        currentlyLoadedImageIdRef.current || currentViewer.selectedImage

      const currentSelectedPointImage = currentViewer.pointDetail?.image360

      const sceneName = Krpano.getScenceNameByFileId(
        currentViewer.selectedPoint,
        points,
        currentViewer.selectedImage
      )

      if (tourViewerKrpano.get(`scene[${sceneName}]`) === null) {
        const screenString = Krpano.generateSceneXML({
          sceneName,
          tilesUrl: currentSelectedPointImage?.tilesUrl || undefined,
          multires: currentSelectedPointImage?.multires || undefined,
          s3Path: currentSelectedPointImage?.s3Path || '',
          thumbnailUrl: currentSelectedPointImage?.multires
            ? `${currentSelectedPointImage?.tilesUrl}/preview.jpg`
            : currentSelectedPointImage?.thumbnail || ''
        })

        tourViewerKrpano.call(
          `includexmlstring(${screenString}, null, null, false)`
        )
      }

      // Restore view: Within same point, always prioritize currentPov (PERSIST)
      const pov = currentPov || currentViewer.pointDetail?.initView
      let targetH = pov?.h
      if (currentPov && currentViewer.pointDetail) {
        const frontSource = Krpano.GetFrontYaw(
          currentViewer.pointDetail,
          sourceImageId
        )
        const frontTarget = Krpano.GetFrontYaw(
          currentViewer.pointDetail,
          mainImageId
        )
        const relativeRotation = currentPov.h - frontSource
        targetH = Krpano.NormalizeAngle(frontTarget + relativeRotation)
      }

      const adjustedInitZ =
        pov?.z !== undefined ? adjustFovForMobileClient(pov.z) : pov?.z

      setCurrentPov({
        h: targetH ?? 0,
        v: pov?.v ?? 0,
        z: adjustedInitZ ?? 100
      })
      setCurrentViewer((prev) => ({
        ...prev,
        selectedImage: mainImageId,
        imageIsLoading: true
      }))
      currentlyLoadedImageIdRef.current = mainImageId || null

      tourViewerKrpano.call(
        `load_scene(${sceneName},${targetH},${pov?.v},${adjustedInitZ})`
      )

      setTimeout(() => {
        setCurrentViewer((prev) => ({
          ...prev,
          imageIsLoading: false
        }))
      }, 500)

      const annotations = currentViewer.pointDetail?.annotations.filter(
        (item) => {
          if (item.imageID) {
            return item.imageID === mainImageId
          }
          return mainImageId === currentViewer.pointDetail?.image360?.id
        }
      )
      annotations &&
        Krpano.AddAnnotation(tourViewerKrpano, annotations, null, true, false)
      directionList && Krpano.AddDirectionArrow(tourViewerKrpano, directionList)
      stairList && Krpano.AddStairway(tourViewerKrpano, stairList)

      // Update URL: clear subimage because we're returning to the main 360 image
      const urlToUpdate = new URL(window.location.href)
      urlToUpdate.searchParams.delete('subimage')
      // Clear pov params too for consistency with different point navigation
      urlToUpdate.searchParams.delete('hlookat')
      urlToUpdate.searchParams.delete('vlookat')
      urlToUpdate.searchParams.delete('fov')
      window.history.pushState({}, '', urlToUpdate.toString())
    } else {
      // DIFFERENT POINT: Before leaving, snapshot current camera into memory for this point
      const leavingPointId = currentViewer.pointDetail?.id
      if (leavingPointId) {
        const h = Number(tourViewerKrpano.get('view.hlookat'))
        const v = Number(tourViewerKrpano.get('view.vlookat'))
        const z = Number(tourViewerKrpano.get('view.fov'))
        if (!Number.isNaN(h) && !Number.isNaN(v) && !Number.isNaN(z)) {
          setPovPerPoint((prev) => ({
            ...prev,
            [leavingPointId]: { h, v, z }
          }))
        }
      }
      // Load Point Cha mới NGAY LẬP TỨC (cách 1):
      // Priority POV: [1] POV đã lưu theo pointId, [2] currentPov hiện tại, [3] initView point mới.
      const selectedPointDetail = points.find((p) => p.id === pointId)
      const targetImageId = selectedPointDetail?.image360?.id
      if (!selectedPointDetail || !targetImageId) return

      const sceneName = Krpano.getScenceNameByFileId(
        pointId,
        points,
        targetImageId
      )
      if (tourViewerKrpano.get(`scene[${sceneName}]`) === null) {
        const screenString = Krpano.generateSceneXML({
          sceneName,
          tilesUrl: selectedPointDetail.image360?.tilesUrl || undefined,
          multires: selectedPointDetail.image360?.multires || undefined,
          s3Path: selectedPointDetail.image360?.s3Path || '',
          thumbnailUrl: selectedPointDetail.image360?.multires
            ? `${selectedPointDetail.image360?.tilesUrl}/preview.jpg`
            : selectedPointDetail.image360?.thumbnail || ''
        })

        tourViewerKrpano.call(
          `includexmlstring(${screenString}, null, null, false)`
        )
      }

      // STRICT RESET: Always reset to the destination point's default view when navigating between points
      const pov = selectedPointDetail.initView
      const adjustedZ =
        pov?.z !== undefined ? adjustFovForMobileClient(pov.z) : pov?.z

      tourViewerKrpano.call(
        `load_scene(${sceneName},${pov?.h},${pov?.v},${adjustedZ})`
      )

      // Reset currentPov so it won't bleed into the next point's onSuccess
      setCurrentPov(null)
      setCurrentSelected(undefined)

      setSelectedPointID(pointId)
      setCurrentViewer((prev) => ({
        ...prev,
        isClickFromTourCarousel: true,
        selectedPoint: pointId,
        selectedArea: `${selectedPointDetail.areaId}`,
        selectedImage: targetImageId
      }))
      currentlyLoadedImageIdRef.current = targetImageId || null

      // Update URL: point/area/floor + clear subimage/h/v/z để tránh reset/default
      const urlToUpdate = new URL(window.location.href)
      if (selectedPointDetail.floorId) {
        urlToUpdate.searchParams.set(
          'floor',
          String(selectedPointDetail.floorId)
        )
      }
      urlToUpdate.searchParams.set('point', pointId)
      urlToUpdate.searchParams.set('area', String(selectedPointDetail.areaId))
      urlToUpdate.searchParams.delete('subimage')
      urlToUpdate.searchParams.delete('hlookat')
      urlToUpdate.searchParams.delete('vlookat')
      urlToUpdate.searchParams.delete('fov')
      window.history.pushState({}, '', urlToUpdate.toString())
    }

    if (isLoadComplete === undefined || isLoadComplete === 'true') {
      setCurrentViewer((prev) => ({
        ...prev,
        isClickFromTourCarousel: true // Indicate this is an external nav (Reset POV unless same point)
      }))
    }
  }

  const handleSelectSubImage = (subImageId: number) => {
    const tourViewerKrpano = document.getElementById('embedpano-viewer') as any

    if (!tourViewerKrpano) return

    const currentSubImageDetails = currentViewer.pointDetail?.subImages?.find(
      (item) => item.id === subImageId
    )

    const targetPointId = currentViewer.selectedPoint
    const targetImage =
      currentViewer.pointDetail?.image360?.id === subImageId
        ? currentViewer.pointDetail?.image360
        : currentSubImageDetails

    const sceneName = Krpano.getScenceNameByFileId(
      targetPointId,
      points,
      subImageId
    )

    if (tourViewerKrpano.get(`scene[${sceneName}]`) === null) {
      const screenString = Krpano.generateSceneXML({
        sceneName,
        tilesUrl: currentSubImageDetails?.tilesUrl || undefined,
        multires: currentSubImageDetails?.multires || undefined,
        s3Path: currentSubImageDetails?.s3Path || '',
        thumbnailUrl: currentSubImageDetails?.multires
          ? `${currentSubImageDetails?.tilesUrl}/preview.jpg`
          : currentSubImageDetails?.thumbnail || ''
      })

      tourViewerKrpano.call(
        `includexmlstring(${screenString}, null, null, false)`
      )
    }

    // Restore view: Within same point, always prioritize currentPov (PERSIST)
    const storedPov = targetPointId ? povPerPoint[targetPointId] : null
    const defaultInitView =
      targetImage?.id === currentViewer.pointDetail?.image360?.id
        ? currentViewer.pointDetail?.initView
        : targetImage?.initView || currentViewer.pointDetail?.initView
    const pov = currentPov || storedPov || defaultInitView
    const sourceImageId =
      currentlyLoadedImageIdRef.current || currentViewer.selectedImage
    let targetH = pov?.h
    if (currentPov && currentViewer.pointDetail) {
      const frontSource = Krpano.GetFrontYaw(
        currentViewer.pointDetail,
        sourceImageId
      )
      const frontTarget = Krpano.GetFrontYaw(
        currentViewer.pointDetail,
        subImageId
      )
      const relativeRotation = currentPov.h - frontSource
      targetH = Krpano.NormalizeAngle(frontTarget + relativeRotation)
    }

    const adjustedInitZ =
      pov?.z !== undefined ? adjustFovForMobileClient(pov.z) : pov?.z

    setCurrentPov({
      h: targetH ?? 0,
      v: pov?.v ?? 0,
      z: adjustedInitZ ?? 100
    })
    setCurrentViewer((prev) => ({
      ...prev,
      imageIsLoading: true
    }))

    tourViewerKrpano.call(
      `load_scene(${sceneName},${targetH},${pov?.v},${adjustedInitZ})`
    )

    // Update current image ID
    currentlyLoadedImageIdRef.current = subImageId

    setTimeout(() => {
      setCurrentViewer((prev) => ({
        ...prev,
        imageIsLoading: false
      }))
    }, 500)

    const annotations = currentViewer.pointDetail?.annotations.filter(
      (item) => {
        if (item.imageID) {
          return item.imageID === subImageId
        }
        return subImageId === currentViewer.pointDetail?.image360?.id
      }
    )
    annotations &&
      Krpano.AddAnnotation(tourViewerKrpano, annotations, null, true, false)
    directionList && Krpano.AddDirectionArrow(tourViewerKrpano, directionList)
    stairList && Krpano.AddStairway(tourViewerKrpano, stairList)

    // Update URL
    const urlToUpdate = new URL(window.location.href)
    if (subImageId && subImageId !== currentViewer.pointDetail?.image360?.id) {
      urlToUpdate.searchParams.set('subimage', subImageId.toString())
    } else {
      urlToUpdate.searchParams.delete('subimage')
    }

    urlToUpdate.searchParams.delete('hlookat')
    urlToUpdate.searchParams.delete('vlookat')
    urlToUpdate.searchParams.delete('fov')

    window.history.pushState({}, '', urlToUpdate.toString())
  }

  return {
    handleThumbnailClick,
    isOpen,
    setIsOpen,
    handleSelectSubImage,
    currentViewer,
    setCurrentViewer
  }
}
